import './ViewTask.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import copyToClipboard from 'copy-text-to-clipboard';

import { ViewTaskProps } from '$core/ViewTask/ViewTask.interface';
import { Api } from '$api';
import { toastActions } from '$store/toast';
import {
  DashboardSectionModel,
  DashboardTaskActionEnum,
  DashboardTaskModel,
  NotifyModelType,
} from '$store/models';
import { Loader, NotifyItem, Truncate } from '$shared/components';
import { Button, Header, Placeholder, ScrollView } from '$uikit';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { goBack, goTo, showModal } from '$navigation/helper';
import { dashboardActions } from '$store/dashboard';
import { mainActions } from '$store/main';
import {
  useHandleCoverSelectCallback,
  useHandlePhotoSelectCallback,
} from '$core/Profile/Profile';
import { classNames as cn, statReachGoal } from '$utils';

export const ViewTask: FC<ViewTaskProps> = (props) => {
  const { taskId } = props;
  const dispatch = useDispatch();
  const t = useTranslator();
  const [loadingStatus, setLoadingStatus] = useState('loading');
  const [info, setInfo] = useState<null | {
    task: DashboardTaskModel;
    section: DashboardSectionModel;
    isActive: boolean;
  }>(null);
  const user = useCurrentUserInfo();
  const [inputUploadKey, setInputUploadKey] = useState<number>(1);

  const loadData = useCallback(() => {
    setLoadingStatus('loading');
    Api.get<any, any>(`/dashboard/task/${taskId}`)
      .then((data) => {
        setInfo({
          task: data.task,
          section: data.section,
          isActive: data.isActive,
        });
        data.users && dispatch(mainActions.setUsers(data.users));
        setLoadingStatus('loaded');
      })
      .catch((e) => {
        setLoadingStatus('failed');
        dispatch(toastActions.setToastFail(e.message));
      });
  }, [taskId]);

  useEffect(() => {
    loadData();
    statReachGoal('task_view', {
      taskId,
    });
  }, [taskId]);

  const handlePhotoSelect = useHandlePhotoSelectCallback(loadData);
  const handleCoverSelect = useHandleCoverSelectCallback(loadData);

  const handleAction = useCallback(async () => {
    if (!info?.task?.action) {
      return;
    }

    statReachGoal('task_action', {
      taskId: info.task.id,
      actionType: info.task.action.type,
    });

    if (info.task.action.type === DashboardTaskActionEnum.ROUTE) {
      goTo(info.task.action.routeName!, info.task.action.routeParams!);
    } else if (info.task.action.type === DashboardTaskActionEnum.MODAL) {
      showModal(
        info.task.action.modalName,
        undefined,
        info.task.action.routeName,
        info.task.action.modalOptions,
      );
    } else if (info.task.action.type === DashboardTaskActionEnum.SET_COVER) {
      showModal('cover');
    } else if (info.task.action.type === DashboardTaskActionEnum.COPY_INVITE_LINK) {
      const hexId = Number(user.id).toString(16);
      const link = `https://${location.host}/${
        user.domain ? user.domain : `id${user.id}`
      }?ref=${hexId}`;
      copyToClipboard(link);
      dispatch(toastActions.setToastSuccess(t('copied')));
    }
  }, [info, handlePhotoSelect, user, t]);

  const handleUploadMedia = useCallback(
    async ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
      if (!info?.task?.action?.type || !files) return;

      if (info.task.action?.type === DashboardTaskActionEnum.SET_PHOTO) {
        handlePhotoSelect(files);
      }

      setInputUploadKey(Date.now());
    },
    [handlePhotoSelect, handleCoverSelect, info, inputUploadKey],
  );

  const handleComplete = useCallback(() => {
    if (info?.task) {
      dispatch(dashboardActions.completeTask(info.task));
      goBack();
    }
  }, [info?.task]);

  const value = useMemo(() => {
    if (!info) {
      return 0;
    }
    return (info.task.currentValue / info.task.finalValue) * 100;
  }, [info]);

  function renderContent() {
    if (loadingStatus === 'loading' || !info) {
      return <Loader isWrapped />;
    }

    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadData}>
              {t('failed_button')}
            </Button>
          }
          centered
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    return (
      <div className="ViewTask">
        <div className="ViewTask__section">{info.section.name}</div>
        <div className="ViewTask__title">{info.task.title}</div>
        {!!info.task.finalValue && (
          <div className="ViewTask__progress">
            <div className="ViewTask__progress__title">
              {!!info.task.reward && (
                <>
                  {t('task_reward')}
                  <div className="ViewTask__reward">+{info.task.reward}€</div>
                </>
              )}
              <div className="ViewTask__progress__label">
                {info.task.currentValue}/{info.task.finalValue}
              </div>
            </div>
            <div className="ViewTask__progress__bar">
              <div
                className={cn('ViewTask__progress__bar__value', {
                  filled: value >= 100,
                })}
                style={{ width: value + '%' }}
              ></div>
            </div>
          </div>
        )}
        <div className="ViewTask__text">
          <Truncate lines={0} cut={false} limit={0} text={info.task.text} />
        </div>
        {renderAction()}
      </div>
    );
  }

  function renderAction() {
    return (
      info?.task.action &&
      info.task.action.type === DashboardTaskActionEnum.WRITE_TO_USER && (
        <div className="ViewTask__userList">
          {info.task.action.options.map((userId) => (
            // <div>{userId}</div>
            <NotifyItem
              notify={{
                id: 0,
                type: NotifyModelType.NEW_SUBSCRIBERS,
                group: '0',
                extra: { userId },
                createdAt: 0,
              }}
              key={userId}
            />
          ))}
        </div>
      )
    );
  }

  return (
    <>
      <Header showBackButton>{t('task_title')}</Header>
      <ScrollView>{renderContent()}</ScrollView>

      {loadingStatus === 'loaded' &&
        !!info &&
        (info.task?.action?.type || (!info.task.isCompleted && info.task.isReached)) &&
        info.task?.action?.type !== DashboardTaskActionEnum.WRITE_TO_USER && (
          <div className="ViewTask__footer">
            {!info.task.isCompleted && info.task.isReached ? (
              <Button mode="pay" onClick={handleComplete}>
                {t('task_button_complete')}
              </Button>
            ) : (
              !info.task.isReached &&
              info.task?.action &&
              (info.task.action.type === DashboardTaskActionEnum.SET_PHOTO ? (
                <div className="ViewTask__attachments__photo_wrap">
                  <Button>{info.task.action.buttonText}</Button>
                  <input
                    key={inputUploadKey}
                    onChange={(e) => handleUploadMedia(e)}
                    accept={'image/*'}
                    type="file"
                  />
                </div>
              ) : (
                <Button onClick={handleAction}>{info.task.action.buttonText}</Button>
              ))
            )}
          </div>
        )}
    </>
  );
};
